import type { SetupNeed } from "@shared/connectors/setupNeed";
import { useEffect, useRef } from "react";
import { SkyButton, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import type { DataflowSummary } from "@shared/types/dataflows";
import { ResetIcon, ValidationSuccessIcon } from "@/lib/icons";
import { ConnectionStateBadge } from "./ConnectionStateBadge";
import { ValidationWait } from "./ValidationWait";

/**
 * The bar that saves a connection (FR-42, UX-4).
 *
 * **Save is never disabled for an unfinished form.** A half-finished connection
 * is a Draft worth keeping, so the control that keeps it cannot be the control
 * that judges it. This is the requirement most likely to be implemented wrong —
 * the reflex is to grey out Save until the form validates — so it is stated
 * here, tested in `connectionEditor.save.test.tsx`, and drawn in canvas 03.
 *
 * The one exception (owner 2026-10-01 23:40) is `saveBlockedReason`: two ticked
 * tables that would land in one warehouse table. The server refuses that save
 * whatever the form holds, so Save waits and the bar says why. `saveDisabledReason`
 * is not about the form: Save waits for a live import (#975) or a running Validate
 * (#1243), whose pass a save pressed meanwhile would lose.
 *
 * It is a `role="toolbar"` pinned below the scroll area: reachable without
 * scrolling the form, and announced as one group of related controls rather
 * than three loose buttons at the end of a long document.
 *
 * #927 (owner, QA 2026-10-13): the answer of Validate is said here, beside the
 * button that asked, in a live region that is always mounted, and focus moves
 * to it without scrolling, so the person and a screen reader both hear it
 * where they are. The message bars above the form still say it at length.
 */

/**
 * The validate button's two labels (#1084): "Validate connection" until a credential is stored,
 * "Re-validate" after. Run causes name the step ("validate the connection again", `VALIDATE_AGAIN_STEP`
 * in `shared/types/importCauses.ts`), never one label; `validateStep.test.ts` holds them in step.
 */
export const VALIDATE_CONNECTION_LABEL = "Validate connection";
export const REVALIDATE_LABEL = "Re-validate";

/** #1098: the answer beside Validate for a saved connection's test of values it does not hold. */
export const UNSAVED_TEST_FAILED = "These values did not pass. Your saved connection is unchanged.";
/** #1098 review: a failed test of unsaved values that a Save would test again, and record. */
export const UNSAVED_TEST_FAILED_SAVE =
  "If you save them, the save tests them again and imports stop until a test passes.";
/**
 * A passing test of unsaved values that a Save tests again. The table list on screen is still the
 * saved connection's until then (#1098 review), so the sentence says where the new one comes from.
 */
export function unsavedTestPassed(entityPluralLower: string): string {
  return `These values passed. Save to keep them. Saving tests them again and then lists the ${entityPluralLower} they found.`;
}
/** A passing test of unsaved values that a Save does not test again (a field the form refuses). */
export const UNSAVED_TEST_PASSED_HELD =
  "These values passed, but a field above is not accepted yet, so a save now does not test them. Fix the field to save them tested.";
/** #1243: Save waits for the Validate that is running, so a pass is never saved as untested. */
export const NOT_WHILE_VALIDATING = "Save is available when Validate finishes.";

/** What the last Validate on this screen answered, as one sentence beside the button. */
export interface SaveBarValidationResult {
  /** Changes whenever a new answer arrives, so focus moves to each one once. */
  key: string;
  intent: "success" | "warning" | "danger" | "neutral";
  text: string;
  /** SD3: a second line beside the answer, e.g. the selected tables a pass removed. */
  extra?: string;
}
export interface SaveBarProps {
  state: DataflowSummary["state"];
  /** "Saved 3 minutes ago", "Unsaved changes", "Not saved yet". */
  status: string;
  isDirty: boolean;
  isSaving: boolean;
  isValidating: boolean;
  /** SD3: the Save in flight also checks the connection, so it says what it waits for. */
  saveChecks?: boolean;
  /** Absent in read-only mode, where the bar is not rendered at all. */
  onSave: () => void;
  onValidate: () => void;
  onDiscard: () => void;
  /** Validate is the only control here that a credential permission gates. */
  canValidate: boolean;
  validateLabel?: string;
  /** Why Save is disabled: only two ticked tables that would share one warehouse table. */
  saveBlockedReason?: string;
  /**
   * Save is off with this reason: a full sentence ("Save is available when the import finishes.",
   * #975, #1348), said as a tooltip and as a line in the bar. Unlike `saveBlockedReason` it is not
   * an error, so it adds no alert line.
   */
  saveDisabledReason?: string;
  /** #927: the last Validate's answer, shown beside the button until the next change. */
  validationResult?: SaveBarValidationResult | null;
  /** #2481: what the setup still needs, in the connector's words. */
  need?: SetupNeed;
}

export function SaveBar({
  state,
  status,
  isDirty,
  isSaving,
  isValidating,
  saveChecks = false,
  onSave,
  onValidate,
  onDiscard,
  canValidate,
  validateLabel = VALIDATE_CONNECTION_LABEL,
  saveBlockedReason,
  saveDisabledReason,
  validationResult = null,
  need = "table",
}: SaveBarProps) {
  const saveButton = (
    <SkyButton
      appearance="primary"
      testId="connection-save"
      loading={isSaving}
      disabled={Boolean(saveBlockedReason) || Boolean(saveDisabledReason)}
      onClick={onSave}
    >
      Save
    </SkyButton>
  );
  const resultRef = useRef<HTMLDivElement>(null);
  const focusedKey = useRef<string | null>(null);
  const resultKey = validationResult?.key ?? null;
  useEffect(() => {
    if (!resultKey || focusedKey.current === resultKey) return;
    focusedKey.current = resultKey;
    resultRef.current?.focus({ preventScroll: true });
  }, [resultKey]);

  return (
    <div
      role="toolbar"
      aria-label="Save connection"
      data-testid="connection-save-bar"
      className="sticky bottom-0 z-20 mt-4 flex flex-wrap items-center gap-3 border-t border-border bg-background px-4 py-3"
    >
      <ConnectionStateBadge state={state} size="large" testId="save-bar-state-badge" need={need} />
      <SkyText size={200} color="muted" testId="save-bar-status">
        {status}
      </SkyText>
      {saveBlockedReason ? (
        <SkyText size={200} color="danger" role="alert" testId="save-bar-blocked">
          {saveBlockedReason}
        </SkyText>
      ) : null}
      {/* The status already says "Enter a Display name to save." (#1198), so it is not said twice. */}
      {/* #1348: the tooltip alone said nothing to a person who does not hover a greyed button, so the
          reason is also a sentence in the bar. Not an alert: nothing is wrong with the form. */}
      {saveDisabledReason && !saveBlockedReason && !status.includes(saveDisabledReason) ? (
        <SkyText size={200} color="muted" testId="save-bar-disabled-reason">
          {saveDisabledReason}
        </SkyText>
      ) : null}
      {/* Wraps at phone width so Save stays inside the bar, never off its right edge. */}
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        {/* Always mounted, so the live region exists before the answer it announces. */}
        <ValidationWait
          active={isValidating || (isSaving && saveChecks)}
          mode={isValidating ? "validate" : "save"}
          testId="save-bar-wait"
        />
        <div
          ref={resultRef}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          data-testid="connection-validate-result"
          className="max-w-md outline-none"
        >
          {validationResult ? (
            <>
              <SkyText
                size={200}
                color={validationResult.intent === "neutral" ? "muted" : validationResult.intent}
              >
                {validationResult.text}
              </SkyText>
              {validationResult.extra ? (
                <SkyText size={200} block testId="connection-validate-extra">
                  {validationResult.extra}
                </SkyText>
              ) : null}
            </>
          ) : null}
        </div>
        <SkyButton
          appearance="subtle"
          icon={ResetIcon}
          testId="connection-discard"
          disabled={!isDirty || isSaving}
          onClick={onDiscard}
        >
          Discard changes
        </SkyButton>
        {canValidate ? (
          <SkyButton
            appearance="secondary"
            icon={ValidationSuccessIcon}
            testId="connection-validate"
            loading={isValidating}
            disabled={isSaving}
            onClick={onValidate}
          >
            {validateLabel}
          </SkyButton>
        ) : null}
        {/* Never `disabled` for an unfinished form (FR-42); only for `saveBlockedReason`, or
            `saveDisabledReason` (a live import, a running Validate). */}
        {saveDisabledReason ? (
          <SkyTooltip content={saveDisabledReason}>
            <span className="inline-flex">{saveButton}</span>
          </SkyTooltip>
        ) : (
          saveButton
        )}
      </div>
    </div>
  );
}
